// components/AllGallery.jsx

import React, { 
    useEffect, 
    useContext, 
    memo, 
    useMemo, 
    useState, 
    useRef, 
    useCallback 
} from 'react';
import { ThemeContext } from '../context/ThemeContext';
import galleryData from '../data/javascripts/galleryData';
import { Helmet } from 'react-helmet-async';
import { Image } from 'lucide-react'; // Icon for placeholder/loading

// --- Configuration Constants ---
const ITEMS_PER_PAGE = 12; // Number of items to load at once

// --- Reusable Gallery Item Component (Replaced with ImageViewerCard logic) ---
const GalleryItemWrapper = memo(({ item, openImageModal }) => {

    // Memoize theme-based class names (Adopted from the ImageViewerCard fix)
    const themeClasses = useMemo(() => ({
        // Shadow-only, no explicit background (matches ArticleCard/ImageViewerCard request)
        cardBg: 'shadow-md hover:shadow-xl transition duration-300 dark:shadow-none', 
        cardTitle: 'text-text-light dark:text-text-dark',
        cardText: 'text-muted-light dark:text-muted-dark',
        badge: 'bg-primary/15 text-primary dark:bg-primary/20 dark:text-primary', 
    }), []);

    // Handle image error
    const handleImageError = useCallback((e) => {
        e.target.onerror = null;
        e.target.src = '/images/default-gallery.webp'; // Fallback image
    }, []);

    // Handle hover effect on the image
    const handleHover = useCallback((e, scale) => {
        e.currentTarget.style.transform = `scale(${scale})`;
    }, []);

    return (
        // Use standard flex/grid classes instead of Bootstrap `col` for better Tailwind compatibility
        <div className="w-full h-full"> 
            <div
                className={`h-full flex flex-col rounded-xl overflow-hidden cursor-pointer ${themeClasses.cardBg}`}
                onClick={() => openImageModal(item)}
                aria-label={`View image: ${item.title}`}
            >
                {/* Image Area */}
                <div className="relative h-60 overflow-hidden">
                    {item.image ? (
                        <img
                            src={item.image}
                            alt={item.title}
                            className="h-full w-full object-cover transition-transform duration-300 ease-in-out"
                            loading="lazy"
                            style={{
                                objectPosition: `center ${typeof item.imageOffsetY === 'number'
                                    ? `${item.imageOffsetY}%`
                                    : item.imageOffsetY || '50%'}`,
                            }}
                            onMouseEnter={(e) => handleHover(e, 1.05)}
                            onMouseLeave={(e) => handleHover(e, 1.0)}
                            onError={handleImageError}
                        />
                    ) : (
                        <div className="flex justify-center items-center h-full bg-gray-200 dark:bg-gray-700">
                            <Image size={32} className="text-gray-400 dark:text-gray-500" />
                        </div>
                    )}
                    <div className="absolute top-3 right-3">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${themeClasses.badge}`}>
                            {item.category}
                        </span>
                    </div>
                </div>

                {/* Text Content */}
                <div className="p-4 flex flex-col flex-grow">
                    <h5 className={`text-lg font-semibold mb-1 ${themeClasses.cardTitle} line-clamp-2 min-h-[2.5rem]`}> 
                        {item.title}
                    </h5>
                    {/* Ensures consistent height for description using line-clamp and flex-grow */}
                    <p className={`text-sm ${themeClasses.cardText} mb-2 line-clamp-2 flex-grow`}> 
                        {item.description}
                    </p>
                    <div className="flex justify-end items-center mt-auto pt-2 border-t border-border-light dark:border-border-dark">
                        <small className={`text-xs ${themeClasses.cardText}`}>
                            {item.date}
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
});


// --- Main Component ---
const AllGallery = ({ openImageModal }) => {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    const { darkMode } = useContext(ThemeContext);
    
    // --- Infinite Scroll States ---
    const loadMoreRef = useRef(null); 
    const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
    const [isLoading, setIsLoading] = useState(false); 

    // --- Data Filtering/Slicing ---
    const allGalleryItems = useMemo(() => galleryData, []);
    
    const itemsToDisplay = useMemo(() => {
        return allGalleryItems.slice(0, displayCount);
    }, [allGalleryItems, displayCount]);

    const hasMore = itemsToDisplay.length < allGalleryItems.length;

    // --- Load More Logic (Same as AllArticles) ---
    const loadMore = useCallback(() => {
        if (hasMore && !isLoading) {
            setIsLoading(true);
            // Simulate delay
            setTimeout(() => {
                setDisplayCount(prevCount => prevCount + ITEMS_PER_PAGE);
                setIsLoading(false);
            }, 500); 
        }
    }, [hasMore, isLoading]);

    // --- Intersection Observer Logic (Same as AllArticles) ---
    useEffect(() => {
        if (!hasMore || !loadMoreRef.current) return;

        const observerTarget = loadMoreRef.current; 

        const observer = new IntersectionObserver(
            (entries) => {
                const target = entries[0];
                if (target.isIntersecting && !isLoading) {
                    loadMore();
                }
            },
            {
                root: null, 
                rootMargin: '100px', 
                threshold: 0.1,
            }
        );

        observer.observe(observerTarget);

        return () => {
            if (observerTarget) {
                observer.unobserve(observerTarget);
            }
        };
    }, [hasMore, isLoading, loadMore]); 

    // --- Theme Classes ---
    const headingClass = `section-heading mb-2 ${darkMode ? 'text-text-dark' : 'text-text-light'}`;
    const containerClass = `mt-12 mb-16 container mx-auto px-4`;
    const loadingTextClass = darkMode ? 'text-muted-dark' : 'text-muted-light';

    const pageBgClass = darkMode ? 'bg-bg-dark' : 'bg-bg-light';


    return (
        <>
            <Helmet>
                <title>Full Photo Gallery & Memories | Varun's Portfolio</title>
                <meta name="description" content="View the complete collection of memories, projects, and experiences in the full gallery." />
                <meta name="keywords" content="full gallery, all photos, memories, personal collection, projects, experiences" />
            </Helmet>

            <div className={`w-full min-h-screen ${pageBgClass}`}>
                <div className={containerClass}>
                    <div className="row">
                        <div className="col-12 text-center mb-4">
                            <h2 className={headingClass}>
                                My Memories
                            </h2>
                        </div>
                    </div>

                    {/* Updated grid for better Tailwind/Responsive handling */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Render Gallery Items */}
                        {itemsToDisplay.map((item, index) => (
                            <GalleryItemWrapper
                                key={item.id || index}
                                item={item}
                                darkMode={darkMode}
                                openImageModal={openImageModal} 
                            />
                        ))}
                    </div>
                    
                    {/* --- Infinite Scroll Anchor / Loading Indicator --- */}
                    {hasMore ? (
                        <div ref={loadMoreRef} className="text-center mt-12">
                            <div className="flex justify-center items-center p-4">
                                {/* Loading Spinner */}
                                <div className="w-8 h-8 border-4 border-primary dark:border-secondary border-t-transparent animate-spin rounded-full"></div>
                                <p className={`ml-3 text-lg ${loadingTextClass}`}>
                                    Loading more photos...
                                </p>
                            </div>
                        </div>
                    ) : (
                        allGalleryItems.length > ITEMS_PER_PAGE && (
                            <div className="text-center mt-12">
                                <p className={`text-lg ${loadingTextClass}`}>
                                    You've reached the end of the gallery.
                                </p>
                            </div>
                        )
                    )}
                </div>
            </div>
        </>
    );
};

export default memo(AllGallery);